import { useMemo } from "react";
import { useSelector } from "react-redux";

import { capitalizeFirst } from "@/helpers/string";
import { RootState } from "@/store";
import { AggregationType, OrderByType, WidgetType } from "@/types/widget";
import { removeSpecialCharacters } from "@/utils/string";

import { DIMENSIONAL_VALUES_SHOWN } from "./constant";

interface UseWidgetConfigurationProps {
  datasetId: string;
  widgetType: WidgetType;
}

const toOption = (value: string, label: string) => ({ value, label });

const ALL_AGGREGATION_OPTIONS = Object.values(AggregationType).map((agg) =>
  toOption(agg, removeSpecialCharacters(agg))
);

const orderByOptions = Object.values(OrderByType).map((orderBy) =>
  toOption(orderBy, orderBy)
);

const dimensionValueShownOptions = DIMENSIONAL_VALUES_SHOWN.map((num) =>
  toOption(num, num)
);

const useWidgetConfiguration = ({
  datasetId,
  widgetType
}: UseWidgetConfigurationProps) => {
  const { datasets } = useSelector((state: RootState) => state.dataset);

  const datasetOptions = useMemo(
    () =>
      Object.values(datasets)
        .reverse()
        .map((dataset) => toOption(dataset.data.id, dataset.data.name)),
    [datasets]
  );

  const dimensionOptions = useMemo(
    () =>
      datasets[datasetId]?.data.schema
        .filter((s) => s.is_dimension)
        .map((s) => toOption(s.attribute_column || "", s.attribute_name)) ?? [],
    [datasets, datasetId]
  );

  // LINE widgets do not support DISTINCT_COUNT aggregation.
  const aggregationOptions = useMemo(
    () =>
      widgetType === WidgetType.LINE
        ? ALL_AGGREGATION_OPTIONS.filter(
            (option) => option.value !== AggregationType.DISTINCT_COUNT
          )
        : ALL_AGGREGATION_OPTIONS,
    [widgetType]
  );

  const measureOptions = useMemo(() => {
    const schema = datasets[datasetId]?.data.schema;
    if (!schema) return [];

    const measureItems = schema
      .filter((s) => s.is_measure)
      .map((s) => toOption(s.attribute_column || "", s.attribute_name));

    if (widgetType === WidgetType.BIG_NUMBER) return measureItems;

    // WIDE_DATA datasets group multiple columns under a shared breakdown_group_id.
    // We surface one option for the whole group rather than listing each column separately.
    const breakdownGroupId = schema.find(
      (s) => s.breakdown_group_id && s.breakdown_group_label
    )?.breakdown_group_id;

    if (!breakdownGroupId) return measureItems;

    return [
      toOption(breakdownGroupId, capitalizeFirst(breakdownGroupId)),
      ...measureItems
    ];
  }, [datasetId, datasets, widgetType]);

  return {
    datasetOptions,
    dimensionOptions,
    measureOptions,
    aggregationOptions,
    orderByOptions,
    dimensionValueShownOptions
  };
};

export default useWidgetConfiguration;
